import {FWU_ERROR_CODES} from '../../consts/fwu/fwuErrorCodes';
import {log} from '../log';
import BaseFwuClient from './baseFwuClient';
import framesProcessingService from '../device/framesProcessingService';
import fwuErrorDetailsService from './fwuErrorDetailsService';

const HID_INPUT_RESPONSE_TIMEOUTS = {
    DEFAULT: 1000,
    FWU_AUTH: 500,
    FWU_END_PROGRAMMING: 2500,
    FWU_PROGRAM_BLOCKS: 1000,
    FWU_START_PROGRAMMING: 2000,
};

const FIRMWARE_STEPS = {
    SUSPEND_MASTER_COMMUNICATION: 'SUSPEND_MASTER_COMMUNICATION',
    AUTHENTICATION: 'AUTHENTICATION',
    START_PROGRAMMING: 'START_PROGRAMMING',
    END_PROGRAMMING: 'END_PROGRAMMING',
    PROGRAMMING: 'PROGRAMMING',
};

let instance = null;

export default class SblFwuClient extends BaseFwuClient {
    constructor(options) {
        if (options && instance) {
            instance.stop();
            instance = null;
        }

        if (instance) {
            return instance;
        }

        super({fwuPackageData: options.fwuPackageData, fwuTarget: options.fwuTarget});
        instance = this;
        this.currentFwuTry = 0;
        this.currentBlockIndex = 0;
        this.onFwuError = options.onFwuError;
        this.onFwuStatusEvent = options.onFwuStatusEvent;

        this.pollingTimeout = null;
        this.pollingTryNumber = 0;
        this.currentTryNumber = 0;
        this.isStopped = true;
    }

    stop = () => {
        if (this.isStopped) return;

        this.isStopped = true;
        this.onFwuStopBase();
        clearTimeout(this.pollingTimeout);
    };

    isMaxTriesExceeded = () => {
        const FWU_MAX_TRIES = 10;

        if (this.currentTryNumber < FWU_MAX_TRIES) {
            this.currentTryNumber = this.currentTryNumber + 1;

            return false;
        }

        this.onFwuFail();
        return true;
    };

    startFwUpdate = (package_index) => {
        this.isStopped = false;
        log.info(`SblFwuClient: start firmware update`);

        this.setStartTs();

        framesProcessingService.restart();

        if (package_index && package_index > 0) {
            this.sendProgramBlocks(package_index);
        } else {
            if (this.fwuPackageData.suspend_master_communication?.f) {
                if (this.isFwuHolderRelatedTarget()) {
                    this.sendSuspendMasterCommunication();
                    return;
                } else {
                    log.warn(
                        'SblFwuClient: there is a suspend_master_communication frame in fwu package but target is not holder'
                    );
                }
            }

            this.sendAuthenticationOrStartProgramming();
        }
    };

    sendAuthenticationOrStartProgramming = () => {
        if (this.fwuPackageData.authentication?.f) {
            this.sendAuthenticationRequest();
        } else {
            this.sendStartProgrammingRequest();
        }
    };

    sendRequestAndSubscribeOnReport = ({blockData, onReport, responseTimeoutMs}) => {
        const {f, v, n} = blockData;
        const onSuccess = (value) => {
            const isValueValid = v ? this.isValidResponse(value, v) : true;
            const onValidResponse = () => {
                this.sendStatusMessage(n);
                onReport();
            };

            if (isValueValid) {
                onValidResponse();
            } else if (this.fwuPackageData.status_polling) {
                this.startPolling(onValidResponse);
            } else {
                log.debug(`SblFwuClient: response frame is not valid: ${value}`);
                this.onHidFwuError({errorFrame: value});
            }
        };

        framesProcessingService.writeFrameAndPublishResponse({
            frames: [f],
            onSuccess,
            onError: this.onFwuFail,
            responseTimeoutMs,
            doNotSplit: true,
        });
    };

    isValidResponse = (response, validValue) => response.toLowerCase().includes(validValue.toLowerCase());

    sendSuspendMasterCommunication = () => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = FIRMWARE_STEPS.SUSPEND_MASTER_COMMUNICATION;
        log.info(`SblFwuClient: send sendSuspendMasterCommunication`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.suspend_master_communication,
            onReport: this.sendAuthenticationOrStartProgramming,
        });
    };

    sendAuthenticationRequest = () => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = FIRMWARE_STEPS.AUTHENTICATION;
        log.info(`SblFwuClient: send sendAuthenticationRequest`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.authentication,
            onReport: this.sendStartProgrammingRequest,
            responseTimeoutMs: HID_INPUT_RESPONSE_TIMEOUTS.FWU_AUTH,
        });
    };

    sendStartProgrammingRequest = () => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = FIRMWARE_STEPS.START_PROGRAMMING;
        log.info(`SblFwuClient: send sendStartProgrammingRequest`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.start_programming,
            onReport: this.sendProgramBlocks,
            responseTimeoutMs: HID_INPUT_RESPONSE_TIMEOUTS.FWU_START_PROGRAMMING,
        });
    };

    sendEndProgrammingRequest = () => {
        this.currentEventType = FIRMWARE_STEPS.END_PROGRAMMING;
        log.info(`SblFwuClient: send sendEndProgrammingRequest`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.end_programming,
            onReport: this.onFwuFinishBase,
            responseTimeoutMs: HID_INPUT_RESPONSE_TIMEOUTS.FWU_END_PROGRAMMING,
        });
    };

    sendProgramBlocks = (blockIndex = 0) => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = FIRMWARE_STEPS.PROGRAMMING;
        log.info(`SblFwuClient: send sendProgramBlocks`);

        this.sendProgramBlock(blockIndex);
    };

    onProgramBlockInputReport = (value) => {
        if (this.isStopped) return;

        const {program_blocks} = this.fwuPackageData;
        const {v, n} = program_blocks[this.currentBlockIndex];
        const isValueValid = this.isValidResponse(value, v);
        const sendNextProgramBlock = () => this.sendProgramBlock(this.currentBlockIndex + 1);

        if (isValueValid) {
            this.sendStatusMessage(n);

            if (this.currentBlockIndex >= program_blocks.length - 1) {
                this.sendEndProgrammingRequest();
            } else {
                sendNextProgramBlock();
            }
        } else if (this.fwuPackageData.status_polling) {
            this.startPolling(() => {
                this.sendProgramBlock(this.currentBlockIndex);
            }, value);
        } else {
            this.onHidFwuError({errorFrame: value, programBlockResponse: value});
        }
    };

    sendProgramBlock = (blockIndex) => {
        this.currentBlockIndex = blockIndex;
        const {f} = this.fwuPackageData.program_blocks[blockIndex];

        framesProcessingService.writeFrameAndPublishResponse({
            frames: [f],
            onSuccess: (frame) => {
                this.onProgramBlockInputReport(frame);
            },
            onError: this.onFwuFail,
            isDebug: false,
            responseTimeoutMs: HID_INPUT_RESPONSE_TIMEOUTS.FWU_PROGRAM_BLOCKS,
            doNotSplit: true,
        });
    };

    startPolling = (onPollingSuccess, programBlockResponse) => {
        log.info(`SblFwuClient: start polling`);

        this.sendPollingRequestAndSubscribeOnReport(onPollingSuccess, programBlockResponse);
    };

    sendPollingRequestAndSubscribeOnReport = (onPollingSuccess, programBlockResponse) => {
        const {busy, counter, delay, read, success} = this.fwuPackageData.status_polling;

        if (this.pollingTryNumber < counter) {
            this.pollingTryNumber = this.pollingTryNumber + 1;
            const onSuccess = (value) => {
                const isSuccess = this.isValidResponse(value, success);

                if (isSuccess) {
                    this.pollingTryNumber = 0;
                    log.info(`SblFwuClient: polling result - success`);

                    onPollingSuccess?.();
                } else {
                    const isBusy = this.isValidResponse(value, busy);

                    if (isBusy) {
                        log.info(`SblFwuClient: polling result - busy`);

                        this.pollingTimeout = setTimeout(() => {
                            this.sendPollingRequestAndSubscribeOnReport(onPollingSuccess, programBlockResponse);
                        }, delay);
                    } else {
                        log.info(`SblFwuClient: polling result - fail`);

                        this.onHidFwuError({errorFrame: value, programBlockResponse});
                    }
                }
            };

            framesProcessingService.writeFrameAndPublishResponse({
                frames: [read],
                onSuccess,
                onError: this.onFwuFail,
                doNotSplit: true,
            });
        } else {
            log.info(`SblFwuClient: polling tries exceeded`);

            this.onFwuFail(programBlockResponse);
        }
    };

    onFwuFail = (programBlockResponse) => {
        this.onHidFwuError({programBlockResponse});
    };

    onHidFwuError = ({errorCode = FWU_ERROR_CODES.GENERAL, errorFrame, programBlockResponse}) => {
        this.stop();

        const errorData = {errorCode, onError: this.onFwuError, errorFrame};
        const errorDetails = fwuErrorDetailsService.getErrorDetails({
            allRequests: this.fwuPackageData.program_blocks,
            currentIndex: this.currentBlockIndex,
            response: programBlockResponse,
        });

        this.onFwuErrorBase({...errorData, ...errorDetails});
    };
}
